import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import sports from "../../sports";
import nfl_logo from "../../assets/nfl.svg";
import crossover_brand_logo from "../../assets/crossover_games_icon.png";
import PageInfo from "../PageInfo/PageInfo";
import styles from "./Nfl170.module.css";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").trim() ||
  "https://crossovergames-8b4eb8e6663f.herokuapp.com";

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${text || res.statusText}`);
  }
  return res.json();
};

const DEFAULT_POSITIONS = ["QB", "RB", "WR", "TE", "OL", "DL", "LB", "DB", "K"];

const FALLBACK_OPP_MODES = [
  { id: "random", label: "Random seasons" },
  { id: "year", label: "One year" },
  { id: "decade", label: "One decade" },
  { id: "gauntlet", label: "Contenders" },
];

export default function Nfl170() {
  const location = useLocation();
  const [phase, setPhase] = useState("setup");
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  const [positions, setPositions] = useState(DEFAULT_POSITIONS);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [seasonLength, setSeasonLength] = useState(17);
  const [opponentMode, setOpponentMode] = useState("year");
  const [opponentYear, setOpponentYear] = useState(2024);

  const [draft, setDraft] = useState(null);
  const [roundIndex, setRoundIndex] = useState(0);
  const [picks, setPicks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api("/api/nfl-sim/config")
      .then((d) => !cancelled && setConfig(d))
      .catch((e) => !cancelled && setError(`Couldn't load setup: ${e.message}`));
    return () => {
      cancelled = true;
    };
  }, []);

  const currentRound = draft?.rounds[roundIndex];

  useEffect(() => {
    if (phase !== "draft" || !currentRound) return;
    const t = setTimeout(() => runSearch(searchQuery), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, roundIndex, phase]);

  const runSearch = async (query) => {
    if (!currentRound) return;
    setSearchLoading(true);
    try {
      const data = await api("/api/nfl-sim/search", {
        method: "POST",
        body: JSON.stringify({
          query,
          position: currentRound.position,
          constraint: currentRound.constraint,
          excludeSlugs: picks.map((p) => p.playerSlug),
          limit: 12,
        }),
      });
      setSearchResults(data.players || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const togglePos = (id) => {
    setSelectedPreset(null);
    setPositions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applyPreset = (preset) => {
    setSelectedPreset(preset.id);
    setPositions(preset.positions);
  };

  const startDraft = async () => {
    if (positions.length === 0) return;
    setError(null);
    try {
      const data = await api("/api/nfl-sim/start", {
        method: "POST",
        body: JSON.stringify({ positions }),
      });
      setDraft(data);
      setRoundIndex(0);
      setPicks([]);
      setSearchQuery("");
      setSearchResults([]);
      setPhase("draft");
    } catch (e) {
      setError(e.message);
    }
  };

  const commitPick = async (player) => {
    if (!currentRound) return;
    const pick = {
      round: currentRound.round,
      position: currentRound.position,
      playerSlug: player.playerSlug,
      playerName: player.playerName,
    };
    try {
      const v = await api("/api/nfl-sim/validate-pick", {
        method: "POST",
        body: JSON.stringify({ pick, constraint: currentRound.constraint }),
      }).catch(() => ({ valid: true }));
      if (v && v.valid === false) {
        setError(`${player.playerName} doesn't satisfy ${currentRound.constraint.label}.`);
        return;
      }
      const next = [...picks, pick];
      setPicks(next);
      setSearchQuery("");
      setSearchResults([]);
      if (roundIndex + 1 < draft.rounds.length) {
        setRoundIndex(roundIndex + 1);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const undoPick = () => {
    if (picks.length === 0) return;
    setPicks(picks.slice(0, -1));
    setRoundIndex(Math.max(0, roundIndex - 1));
    setSearchQuery("");
  };

  const simulate = async () => {
    setSimulating(true);
    setError(null);
    try {
      const payload = { lineup: picks, seasonLength, opponentMode };
      if (opponentMode === "year") payload.opponentYear = opponentYear;
      const data = await api("/api/nfl-sim/simulate", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setSimResult(data);
      setPhase("results");
    } catch (e) {
      setError(e.message);
    } finally {
      setSimulating(false);
    }
  };

  const reset = () => {
    setPhase("setup");
    setDraft(null);
    setRoundIndex(0);
    setPicks([]);
    setSimResult(null);
    setSearchQuery("");
    setSearchResults([]);
    setError(null);
  };

  const allPositions =
    config?.positions || DEFAULT_POSITIONS.map((id) => ({ id, label: id }));
  const page_name = location?.pathname || "";

  return (
    <>
      <div className={styles.page}>
        <nav className={styles.navbar}>
          <a
            href="https://crossovergrid.com"
            className={styles.navLink}
            aria-label="Crossover Grid home"
          >
            <img src={nfl_logo} alt="NFL" className={styles.navLogo} />
            <span className={styles.navTitle}>CROSSOVER NFL</span>
          </a>
        </nav>

        <div className={styles.sportPicker}>
          <div className={styles.sportPickerInner}>
            {sports.map((sport) => (
              <Link
                key={sport.id}
                className={`${styles.sportLink} ${page_name === sport.id ? styles.sportLinkActive : ""}`}
                to={sport.path}
              >
                {sport.emoji && sport.emoji.startsWith("http") ? (
                  <img src={sport.emoji} alt={sport.name} />
                ) : (
                  <span>{sport.emoji}</span>
                )}
                <br />
                <span style={{ fontSize: sport.name.length < 4 ? 13 : 11 }}>
                  {sport.name}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className={styles.game}>
          <header className={styles.header}>
            <div className={styles.mark}>
              <span className={styles.tag}>SEASON SIM · NFL</span>
              <h1 className={styles.title}>17-0</h1>
              <span className={styles.sub}>Draft a lineup. Beat history.</span>
            </div>
            {phase !== "setup" && (
              <button className={styles.btnGhost} onClick={reset} type="button">
                ← New sim
              </button>
            )}
          </header>

          {error && (
            <div className={styles.alert} role="alert">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label="Dismiss">
                ×
              </button>
            </div>
          )}

          {phase === "setup" && (
            <SetupView
              styles={styles}
              config={config}
              positions={positions}
              allPositions={allPositions}
              selectedPreset={selectedPreset}
              seasonLength={seasonLength}
              opponentMode={opponentMode}
              opponentYear={opponentYear}
              onTogglePos={togglePos}
              onApplyPreset={applyPreset}
              onSeasonLength={setSeasonLength}
              onOpponentMode={setOpponentMode}
              onOpponentYear={setOpponentYear}
              onStart={startDraft}
            />
          )}

          {phase === "draft" && draft && (
            <DraftView
              styles={styles}
              draft={draft}
              roundIndex={roundIndex}
              currentRound={currentRound}
              picks={picks}
              searchQuery={searchQuery}
              searchResults={searchResults}
              searchLoading={searchLoading}
              seasonLength={seasonLength}
              onSearch={setSearchQuery}
              onPick={commitPick}
              onUndo={undoPick}
              onSimulate={simulate}
              simulating={simulating}
            />
          )}

          {phase === "results" && simResult && (
            <ResultsView styles={styles} result={simResult} onPlayAgain={reset} />
          )}

          <a
            href="https://crossover.games"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.presentedBy}
          >
            <span className={styles.presentedByLabel}>Presented by</span>
            <img
              src={crossover_brand_logo}
              alt="Crossover Games"
              className={styles.presentedByLogo}
            />
            <span className={styles.presentedByName}>Crossover.Games</span>
          </a>
        </div>
      </div>

      <PageInfo
        title="17-0 — NFL Season Sim | Crossover Grid"
        metaTitle="17-0 — NFL Season Sim"
        metaDescription="Build an all-time NFL lineup and see if you can go 17-0. Pick legends position by position, then simulate a full season against real NFL team-years."
        metaUrl={location.pathname}
        keywords={["nfl simulator", "17-0", "perfect season", "nfl game", "fantasy nfl", "all-time nfl lineup"]}
        twitterCreator="@CrossoverGrid"
      />
    </>
  );
}

// ---------- Setup ----------
function SetupView({
  styles,
  config,
  positions,
  allPositions,
  selectedPreset,
  seasonLength,
  opponentMode,
  opponentYear,
  onTogglePos,
  onApplyPreset,
  onSeasonLength,
  onOpponentMode,
  onOpponentYear,
  onStart,
}) {
  const seasonLengths = config?.seasonLengths || [8, 12, 17];
  const opponentModes = config?.opponentModes || FALLBACK_OPP_MODES;
  const yearRange = config?.opponentYearRange || { min: 1960, max: 2024 };
  const presets = config?.presets || [];

  return (
    <div className={styles.setup}>
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>01 · LINEUP</span>
          <span className={styles.cardMeta}>
            {positions.length} slot{positions.length === 1 ? "" : "s"}
          </span>
        </div>

        {presets.length > 0 && (
          <div className={styles.presets}>
            {presets.map((p) => (
              <button
                key={p.id}
                className={`${styles.chip} ${selectedPreset === p.id ? styles.chipActive : ""}`}
                onClick={() => onApplyPreset(p)}
                type="button"
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        <div className={styles.posGrid}>
          {allPositions.map((p) => (
            <button
              key={p.id}
              className={`${styles.pos} ${positions.includes(p.id) ? styles.posOn : ""}`}
              onClick={() => onTogglePos(p.id)}
              title={p.description || ""}
              type="button"
            >
              <span className={styles.posLabel}>{p.label || p.id}</span>
              {p.description && (
                <span className={styles.posDesc}>{p.description}</span>
              )}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>02 · SEASON LENGTH</span>
        </div>
        <div className={styles.seg}>
          {seasonLengths.map((n) => (
            <button
              key={n}
              className={`${styles.segBtn} ${seasonLength === n ? styles.segBtnActive : ""}`}
              onClick={() => onSeasonLength(n)}
              type="button"
            >
              {n} games
            </button>
          ))}
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>03 · OPPONENTS</span>
        </div>
        <div className={`${styles.seg} ${styles.segWide}`}>
          {opponentModes.map((m) => (
            <button
              key={m.id}
              className={`${styles.segBtn} ${opponentMode === m.id ? styles.segBtnActive : ""}`}
              onClick={() => onOpponentMode(m.id)}
              type="button"
            >
              {m.label}
            </button>
          ))}
        </div>
        {opponentMode === "year" && (
          <div className={styles.year}>
            <label htmlFor="n170-year">YEAR</label>
            <input
              id="n170-year"
              type="number"
              min={yearRange.min}
              max={yearRange.max}
              value={opponentYear}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isNaN(v)) return;
                onOpponentYear(
                  Math.max(yearRange.min, Math.min(yearRange.max, v))
                );
              }}
            />
            <span className={styles.yearRange}>
              {yearRange.min}–{yearRange.max}
            </span>
          </div>
        )}
      </section>

      <button
        className={styles.cta}
        disabled={positions.length === 0}
        onClick={onStart}
        type="button"
      >
        Start draft
        <span className={styles.ctaArrow}>→</span>
      </button>
    </div>
  );
}

// ---------- Draft ----------
function DraftView({
  styles,
  draft,
  roundIndex,
  currentRound,
  picks,
  searchQuery,
  searchResults,
  searchLoading,
  seasonLength,
  onSearch,
  onPick,
  onUndo,
  onSimulate,
  simulating,
}) {
  const total = draft.rounds.length;
  const isComplete = picks.length === total;
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isComplete && inputRef.current) inputRef.current.focus();
  }, [roundIndex, isComplete]);

  return (
    <div className={styles.draft}>
      <aside className={styles.lineupSide}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>LINEUP</span>
          <span className={styles.cardMeta}>
            {picks.length}/{total}
          </span>
        </div>
        <ol className={styles.slotList}>
          {draft.rounds.map((r, idx) => {
            const pick = picks[idx];
            const isCurrent = idx === roundIndex && !isComplete;
            return (
              <li
                key={idx}
                className={`${styles.slot} ${pick ? styles.slotFilled : ""} ${isCurrent ? styles.slotCurrent : ""}`}
              >
                <span className={styles.slotNum}>
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className={styles.slotBody}>
                  <span className={styles.slotPos}>{r.position}</span>
                  {pick ? (
                    <span className={styles.slotName}>{pick.playerName}</span>
                  ) : (
                    <span className={styles.slotEmpty}>— empty —</span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {picks.length > 0 && !isComplete && (
          <button
            className={`${styles.btnGhost} ${styles.undo}`}
            onClick={onUndo}
            type="button"
          >
            ↶ Undo last
          </button>
        )}

        {isComplete && (
          <button
            className={`${styles.cta} ${styles.ctaSim}`}
            onClick={onSimulate}
            disabled={simulating}
            type="button"
          >
            {simulating ? "Simulating…" : `Simulate ${seasonLength}`}
            {!simulating && <span className={styles.ctaArrow}>→</span>}
          </button>
        )}
      </aside>

      <main className={styles.board}>
        {!isComplete && currentRound && (
          <>
            <div className={styles.hud}>
              <div className={styles.hudCell}>
                <span className={styles.hudLabel}>ROUND</span>
                <span className={styles.hudBig}>
                  {currentRound.round}
                  <span className={styles.hudMid}>/{total}</span>
                </span>
              </div>
              <div className={styles.hudDivider} />
              <div className={styles.hudCell}>
                <span className={styles.hudLabel}>POSITION</span>
                <span className={styles.hudBig}>{currentRound.position}</span>
              </div>
              <div className={styles.hudDivider} />
              <div className={`${styles.hudCell} ${styles.hudCn}`}>
                <span className={styles.hudLabel}>CONSTRAINT</span>
                <span className={styles.hudCnVal}>
                  {currentRound.constraint?.label || "—"}
                </span>
              </div>
            </div>

            <div className={styles.search}>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={`Search eligible ${currentRound.position}s…`}
                autoComplete="off"
                spellCheck={false}
              />
              {searchLoading && <span className={styles.spinner} aria-hidden />}
            </div>

            <div className={styles.results}>
              {searchResults.length === 0 && !searchLoading && (
                <div className={styles.resultsEmpty}>
                  {searchQuery
                    ? "No eligible players match."
                    : "Type a name to find eligible players for this slot."}
                </div>
              )}
              {searchResults.map((p) => (
                <button
                  key={p.playerSlug}
                  className={styles.pcard}
                  onClick={() => onPick(p)}
                  type="button"
                >
                  <div className={styles.pcardMain}>
                    <span className={styles.pcardName}>{p.playerName}</span>
                    <span className={styles.pcardMeta}>
                      <strong>{p.position}</strong>
                      {p.teams?.length > 0 && (
                        <>
                          {" "}
                          · {p.teams.slice(0, 3).join(" / ")}
                          {p.teams.length > 3 ? "…" : ""}
                        </>
                      )}
                      {p.latestYear && <> · {p.latestYear}</>}
                    </span>
                    {p.college && (
                      <span className={styles.pcardSub}>{p.college}</span>
                    )}
                  </div>
                  <div className={styles.pcardStats}>
                    {p.bestAv != null && (
                      <div className={styles.stat}>
                        <span className={styles.statNum}>{p.bestAv}</span>
                        <span className={styles.statLab}>AV</span>
                      </div>
                    )}
                    {p.seasons != null && (
                      <div className={styles.stat}>
                        <span className={styles.statNum}>{p.seasons}</span>
                        <span className={styles.statLab}>SZN</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {isComplete && (
          <div className={styles.readyup}>
            <span className={styles.tag}>READY</span>
            <h2>Lineup locked in.</h2>
            <p>Hit Simulate to run a {seasonLength}-game season.</p>
          </div>
        )}
      </main>
    </div>
  );
}

// ---------- Results ----------
function ResultsView({ styles, result, onPlayAgain }) {
  const { summary = {}, games = [], lineup = [] } = result;
  const winRate = summary.seasonLength
    ? Math.round((summary.wins / summary.seasonLength) * 1000) / 10
    : 0;
  const diff = summary.pointDifferential;

  return (
    <div className={styles.resultsScreen}>
      <section className={styles.scoreboard}>
        <div className={styles.rec}>
          <span className={styles.recVal}>
            {summary.wins}
            <span className={styles.recSep}>-</span>
            {summary.losses}
          </span>
          <span className={styles.recLab}>
            {summary.undefeated
              ? "PERFECT SEASON"
              : `${summary.seasonLength}-GAME RECORD`}
          </span>
        </div>
        <div className={styles.statline}>
          <Stat styles={styles} label="RATING" val={summary.teamRating} />
          <Stat styles={styles} label="POINTS FOR" val={summary.pointsFor} />
          <Stat styles={styles} label="POINTS AGAINST" val={summary.pointsAgainst} />
          <Stat
            styles={styles}
            label="DIFF"
            val={diff != null ? (diff > 0 ? `+${diff}` : `${diff}`) : "—"}
            accent={diff > 0 ? "pos" : diff < 0 ? "neg" : null}
          />
          <Stat styles={styles} label="WIN %" val={`${winRate}%`} />
        </div>
        {summary.opponentMode === "year" && summary.opponentYear && (
          <div className={styles.oppMeta}>VS · {summary.opponentYear} SLATE</div>
        )}
        {summary.opponentMode && summary.opponentMode !== "year" && (
          <div className={styles.oppMeta}>
            VS · {String(summary.opponentMode).toUpperCase()}
          </div>
        )}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>LINEUP</span>
          <span className={styles.cardMeta}>{lineup.length} picks</span>
        </div>
        <div className={styles.lineupRecap}>
          {lineup.map((p, i) => (
            <div key={i} className={styles.lineupRecapRow}>
              <span className={styles.slotNum}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className={styles.slotPos}>{p.position}</span>
              <span className={styles.slotName}>{p.playerName}</span>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>WEEK BY WEEK</span>
          <span className={styles.cardMeta}>{games.length} games</span>
        </div>
        <div className={styles.games}>
          {games.map((g) => (
            <div
              key={g.week}
              className={`${styles.game} ${g.won ? styles.gameW : styles.gameL}`}
            >
              <span className={styles.gameWk}>W{g.week}</span>
              <div className={styles.gameOpp}>
                <span className={styles.gameTeam}>
                  {g.opponent?.teamCode || "—"}
                </span>
                <span className={styles.gameSub}>
                  {g.opponent?.year}
                  {g.opponent?.record && <> · {g.opponent.record}</>}
                  {g.opponent?.playoffResult && <> · {g.opponent.playoffResult}</>}
                </span>
              </div>
              <div className={styles.gameScore}>
                <span>{g.userScore}</span>
                <span className={styles.gameDash}>—</span>
                <span>{g.opponentScore}</span>
              </div>
              <span
                className={`${styles.gameResult} ${g.won ? styles.gameW : styles.gameL}`}
              >
                {g.won ? "W" : "L"}
              </span>
            </div>
          ))}
        </div>
      </section>

      <button className={styles.cta} onClick={onPlayAgain} type="button">
        Run it back
        <span className={styles.ctaArrow}>↻</span>
      </button>
    </div>
  );
}

function Stat({ styles, label, val, accent }) {
  const accentClass =
    accent === "pos"
      ? styles.bigstatPos
      : accent === "neg"
        ? styles.bigstatNeg
        : "";
  return (
    <div className={`${styles.bigstat} ${accentClass}`}>
      <span className={styles.bigstatVal}>{val ?? "—"}</span>
      <span className={styles.bigstatLab}>{label}</span>
    </div>
  );
}
